import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle } from 'lucide-react'
import { FormInput, SubmitButton } from '@/components/form'
import { organizationsService } from '@/services/organizations'
import { ROUTES } from '@/constants/routes'
import { authService } from '@/services/auth'
import { getApiErrorMessage } from '@/utils/errors'

const ORGANIZATION_ID_KEY = 'custom_offer_organization_id'

export function CustomOfferFormPage() {
  const { t } = useTranslation('wizard')
  const navigate = useNavigate()
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [desiredLicensesCount, setDesiredLicensesCount] = useState<string>('')
  const [desiredBillingDetails, setDesiredBillingDetails] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const id = sessionStorage.getItem(ORGANIZATION_ID_KEY)
    if (id) {
      setOrganizationId(id)
    } else if (!authService.getAccessToken()) {
      navigate(ROUTES.HOME, { replace: true })
    } else {
      setError(t('customOfferForm.errors.noOrganization'))
    }
  }, [navigate, t])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organizationId) return

    const count = parseInt(desiredLicensesCount, 10)
    if (isNaN(count) || count < 1) {
      setError(t('customOfferForm.errors.invalidLicenses'))
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await organizationsService.updateDesiredPricing(organizationId, {
        desired_licenses_count: count,
        ...(desiredBillingDetails.trim() && {
          desired_billing_details: desiredBillingDetails.trim(),
        }),
      })
      setSuccess(true)
      sessionStorage.removeItem(ORGANIZATION_ID_KEY)
    } catch (err) {
      setError(getApiErrorMessage(err, t('customOfferForm.errors.generic')))
    } finally {
      setIsLoading(false)
    }
  }

  if (organizationId === null && !error) {
    return null
  }

  if (success) {
    return (
      <div className="bg-background">
        <div className="mx-auto mt-10 max-w-360 px-4 pb-10">
          <div className="mx-auto max-w-4xl">
            <div className="mx-auto max-w-2xl">
              <div className="mb-10 rounded-2xl border border-[#E9E9E9] bg-white p-6 shadow-[0px_-1px_4px_rgba(0,0,0,0.25)]">
                <div className="mb-4 flex items-start gap-3 rounded-lg border border-green-300 bg-green-50 p-4">
                  <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                  <p className="text-sm font-normal text-green-600">
                    {t('customOfferForm.successMessage')}
                  </p>
                </div>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => navigate(ROUTES.HOME)}
                    className="bg-primary text-primary-foreground cursor-pointer rounded-lg px-10 py-2.5 text-sm font-semibold transition-opacity hover:opacity-90"
                  >
                    {t('accountCreated.customOffer.finishButton')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-360 px-4 pb-10">
        <div className="mx-auto mt-10 max-w-4xl">
          <div className="mb-10 text-center">
            <h1 className="text-foreground mb-4 text-xl font-bold">{t('customOfferForm.title')}</h1>
            <p className="text-neutral-gray text-base font-normal">
              {t('customOfferForm.description')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mx-auto max-w-2xl">
            <div className="mb-10 rounded-2xl border border-[#E9E9E9] bg-white p-6 shadow-[0px_-1px_4px_rgba(0,0,0,0.25)]">
              {error && (
                <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                  <p className="text-sm font-normal text-red-600">{error}</p>
                </div>
              )}

              <FormInput
                id="desired-licenses"
                label={t('customOfferForm.desiredLicensesLabel')}
                required
                type="number"
                min={1}
                value={desiredLicensesCount}
                onChange={e => setDesiredLicensesCount(e.target.value)}
                placeholder={t('customOfferForm.desiredLicensesPlaceholder')}
                disabled={isLoading}
                className="mb-4"
              />

              <div className="mb-6">
                <label
                  htmlFor="desired-details"
                  className="mb-2 block text-sm font-semibold text-black"
                >
                  {t('customOfferForm.detailsLabel')}
                </label>
                <textarea
                  id="desired-details"
                  rows={4}
                  value={desiredBillingDetails}
                  onChange={e => setDesiredBillingDetails(e.target.value)}
                  placeholder={t('customOfferForm.detailsPlaceholder')}
                  className="w-full rounded-lg border border-[#858C94] bg-white px-4 py-3.5"
                  disabled={isLoading}
                />
              </div>

              <SubmitButton
                isLoading={isLoading}
                loadingLabel={t('rppsVerification.structureLoading')}
              >
                {t('customOfferForm.submitButton')}
              </SubmitButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
